import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { setAppLanguage, type AppLanguage } from "@/lib/i18n";
import { useOnboardingStore } from "@/stores/onboarding";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/onboarding/welcome")({
  head: () => ({
    meta: [{ title: "Welcome" }],
  }),
  component: WelcomeStep,
});

function WelcomeStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language = useOnboardingStore((s) => s.language);
  const setLanguage = useOnboardingStore((s) => s.setLanguage);
  const setStep = useOnboardingStore((s) => s.setStep);

  function pick(lng: AppLanguage) {
    setLanguage(lng);
    setAppLanguage(lng);
  }

  return (
    <div className="flex flex-1 flex-col">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
        {t("onboarding.welcome.eyebrow")}
      </p>
      <h1 className="font-display text-3xl font-bold tracking-normal">
        {t("onboarding.welcome.title")}
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        {t("onboarding.welcome.body")}
      </p>

      <div className="mt-8 grid gap-3">
        {(
          [
            ["en", t("common.english")],
            ["sw", t("common.swahili")],
          ] as const
        ).map(([code, label]) => (
          <button
            key={code}
            type="button"
            onClick={() => pick(code)}
            className={cn(
              "rounded-lg border px-4 py-3 text-left text-sm font-semibold transition-colors",
              language === code
                ? "border-primary bg-primary-soft text-foreground"
                : "border-border bg-card hover:border-primary/40",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-auto pt-10">
        <Button
          className="w-full"
          onClick={() => {
            setStep("verify");
            void navigate({ to: "/signup" });
          }}
        >
          {t("onboarding.welcome.cta")}
        </Button>
      </div>
    </div>
  );
}
